import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getRoles,
  getRolesCount,
  getRole,
  createRole,
  updateRole,
  deleteRole,
  checkRoleIdUnique,
  getSchemaModels,
  getSchemaFields,
  getRoleChangelog,
} from '../api';
import { roleKeys } from './queryKeys';
import type { RolesFilters, CreateRoleInput, UpdateRoleInput } from '../types';

/**
 * Helper to extract error message from unknown error
 */
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Hook to fetch roles with filters
 */
export function useRoles(filters: RolesFilters = {}) {
  return useQuery({
    queryKey: roleKeys.list(filters),
    queryFn: () => getRoles(filters),
  });
}

/**
 * Hook to fetch accurate total count of roles
 */
export function useRolesCount(filters: RolesFilters = {}) {
  return useQuery({
    queryKey: [...roleKeys.lists(), 'count', filters] as const,
    queryFn: () => getRolesCount(filters),
  });
}

/**
 * Hook to fetch a single role by ID
 */
export function useRole(roleId: string | undefined) {
  return useQuery({
    queryKey: roleKeys.detail(roleId!),
    queryFn: () => getRole(roleId!),
    enabled: !!roleId,
  });
}

/**
 * Hook to fetch schema models for permission builder
 */
export function useSchemaModels() {
  return useQuery({
    queryKey: roleKeys.schemas(),
    queryFn: getSchemaModels,
    staleTime: 30 * 60 * 1000, // Cache for 30 minutes
  });
}

/**
 * Hook to fetch schema fields for a specific model
 */
export function useSchemaFields(modelName: string | undefined) {
  return useQuery({
    queryKey: roleKeys.schemaFields(modelName!),
    queryFn: () => getSchemaFields(modelName!),
    enabled: !!modelName,
    staleTime: 30 * 60 * 1000, // Cache for 30 minutes
  });
}

/**
 * Hook to fetch changelog for a role
 */
export function useRoleChangelog(roleId: string | undefined) {
  return useQuery({
    queryKey: [...roleKeys.detail(roleId!), 'changelog'] as const,
    queryFn: () => getRoleChangelog(roleId!),
    enabled: !!roleId,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Hook to create a new role
 */
export function useCreateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRoleInput) => createRole(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
      toast.success('Role created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create role', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to update a role
 */
export function useUpdateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ roleId, data }: { roleId: string; data: UpdateRoleInput }) =>
      updateRole(roleId, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roleKeys.detail(variables.roleId) });
      toast.success('Role updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update role', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to delete a role
 */
export function useDeleteRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (roleId: string) => deleteRole(roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
      toast.success('Role deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete role', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to check roleId uniqueness
 */
export function useCheckRoleIdUnique() {
  return useMutation({
    mutationFn: ({ roleId, excludeId }: { roleId: string; excludeId?: string }) =>
      checkRoleIdUnique(roleId, excludeId),
  });
}
